"use client";

import { Fragment, useRef, useState } from "react";
import { useScroll, useTransform, motion } from "framer-motion";
import { RxChevronRight } from "react-icons/rx";
import Section from "@/components/static/Section";
import Headline from "@/components/static/Headline";
import Text from "@/components/static/Text";
import parse from "html-react-parser";
import Image from "next/image";
import Buttons from "@/components/static/Buttons";
import Button from "@/components/static/Button";
import { SectionProps, HeadlineProps, ImageProps, ButtonProps } from "@/lib/types";
import { cn } from "@/lib/utils";

//@ts-ignore
import { Splide, SplideSlide } from "@splidejs/react-splide";
import "@splidejs/react-splide/css";
import { Pagination } from "@relume_io/relume-ui";

type Content = {
    headline: HeadlineProps;
    text: string;
    image: ImageProps;
    settings: SectionProps;
    buttons: ButtonProps[];
    inners: TimelineProps[];
};

type TimelineProps = {
    year: string;
    headline: string;
    text: string;
    image: ImageProps;
};

export default function Timeline_2({ content }: { content: Content }) {
    const splideRef = useRef<any>(null);
    const [activeSlide, setActiveSlide] = useState(0);

    const handleSlideChange = (splide: any) => {
        setActiveSlide(splide.index); // Aktuellen Slide-Index setzen
    };

    const handlePaginationClick = (index: number) => {
        splideRef.current?.splide.go(index); // Zu einem bestimmten Slide navigieren
    };
    return (
        <Section dataComponent='Timeline_2' settings={content.settings}>
            <div className='container'>
                <div className='relative'>
                    <div className='flex flex-col'>
                        <div className='order-2 lg:order-1'>
                            <Splide
                                ref={splideRef}
                                options={{
                                    type: "slide",
                                    perPage: 1,
                                    rewind: false,
                                    arrows: false,
                                    perMove: 1,
                                    pagination: false,
                                    speed: 1000,
                                }}
                                onMove={handleSlideChange}
                            >
                                {content.inners.map((timeline, index) => (
                                    <SplideSlide key={index}>
                                        <Timeline key={index} timeline={timeline} />
                                    </SplideSlide>
                                ))}
                            </Splide>
                        </div>

                        <div className='custom-pagination order-1 lg:order-2 flex justify-between mt-0 mb-16 lg:mb-0 lg:mt-16'>
                            {content.inners.map((year, index) => (
                                <>
                                    {/* Zeitstrahl */}
                                    <div className='h-[3px] w-full bg-grey relative overflow-hidden mt-[6px]'>
                                        <div
                                            className={`absolute h-full bg-brand-light transition-all duration-500`}
                                            style={{
                                                width: index <= activeSlide ? "100%" : "0%",
                                            }}
                                        ></div>
                                    </div>
                                    <button
                                        key={index}
                                        className={`flex items-center w-max ${
                                            index === activeSlide ? "text-primary" : "text-primary"
                                        }`}
                                        onClick={() => handlePaginationClick(index)}
                                    >
                                        <div className='flex justify-center min-w-14 flex-col gap-2 items-center'>
                                            <div
                                                className={`size-4 rounded-full block ${
                                                    index === activeSlide
                                                        ? "bg-brand-dark"
                                                        : "bg-grey"
                                                }`}
                                            ></div>
                                            <div className='text-[24px]'>{year.year}</div>
                                        </div>
                                    </button>
                                </>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </Section>
    );
}

const Timeline = ({ timeline }: { timeline: TimelineProps }) => {
    return (
        <SplideSlide>
            <div className='grid lg:grid-cols-2 gap-20 items-center'>
                <div>
                    <div className='text-h0 text-primary font-medium mb-4 leading-[120%]'>
                        {timeline.year}
                    </div>
                    <div className='text-h4 text-primary mb-6  leading-[120%]'>
                        {timeline.headline}
                    </div>
                    <p className='text-textSecondary'>{timeline.text}</p>
                </div>
                <div>
                    <div className='aspect-square rounded-[32px] overflow-hidden'>
                        <Image
                            src={timeline.image.url}
                            height={timeline.image.height}
                            width={timeline.image.width}
                            alt={timeline.image.alt}
                            className='size-full object-cover'
                        />
                    </div>
                </div>
            </div>
        </SplideSlide>
    );
};
